import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TemplateVariable } from '@troia-v3/types';
import { ProviderId } from '@troia-v3/types';
import { useTemplate, useCreateTemplate, useUpdateTemplate } from '../hooks/useTemplates';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { Select, Textarea, Input, Tabs } from '@/shared/components/ui';
import type { TabItem } from '@/shared/components/ui/Tabs';
import {
  WhatsAppBusinessForm,
  GatewayForm,
  SocialMediaForm,
  VariableDefinitionEditor,
  type VariableDefinition
} from '../components';
import { TemplatePreview } from '../components/TemplatePreview';
import { renderTemplateSeguro, valoresDeExemplo } from '../components/templateRender';
import { EmailContentTab } from '../components/EmailContentTab';
import { ImproveWithAIModal } from '../components/ImproveWithAIModal';
import { useGenerateHtml } from '../hooks/useGenerateHtml';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

const EMAIL_PROVIDERS: string[] = [
  ProviderId.EMAIL_SMTP, ProviderId.EMAIL_SENDGRID, ProviderId.EMAIL_SES,
  ProviderId.EMAIL_RESEND, ProviderId.GMAIL_API,
];
const isEmailProvider = (pid: string | null): boolean => !!pid && EMAIL_PROVIDERS.includes(pid);

type TabType = 'basic' | 'email-content' | 'provider' | 'variables';

/**
 * Formatos de cabeçalho do WhatsApp oficial que carregam arquivo. Salvar um
 * deles sem arquivo produz um template que a Meta recusa depois, com um erro
 * que não explica nada (400, code 100, subcode 2388043) — por isso a tela
 * barra antes de enviar.
 */
const FORMATOS_DE_CABECALHO_COM_ARQUIVO = ['IMAGE', 'VIDEO', 'DOCUMENT'];

interface ComponenteDoTemplate {
  type?: string;
  format?: string;
  example?: { header_handle?: string[] };
}

/**
 * Devolve a mensagem de erro quando o cabeçalho de mídia está sem arquivo, ou
 * `null` quando está tudo certo. De quebra, limpa um `headerMedia` sem URL, que
 * o Zod do backend recusaria com uma mensagem técnica.
 */
const erroDoAnexoDoCabecalho = (config: Record<string, unknown> | undefined): string | null => {
  if (!config || config.providerType !== 'whatsapp_business') return null;

  const anexo = config.headerMedia as { url?: string } | undefined;
  const url = (anexo?.url ?? '').trim();

  const componentes = (config.components ?? []) as ComponenteDoTemplate[];
  const cabecalhoComArquivo = componentes.find(
    (c) => c.type === 'HEADER' && !!c.format && FORMATOS_DE_CABECALHO_COM_ARQUIVO.includes(c.format),
  );

  // Template já aprovado guarda o identificador do arquivo de exemplo que a
  // Meta devolveu (`example.header_handle`). Nesse caso o arquivo já existe do
  // lado da Meta e o backend não exige URL — a tela também não pode exigir,
  // senão editar um template aprovado fica impossível.
  const jaTemIdentificador = (cabecalhoComArquivo?.example?.header_handle?.length ?? 0) > 0;

  if (cabecalhoComArquivo && !jaTemIdentificador && url === '') {
    return 'O cabeçalho de mídia está sem arquivo. Escolha o arquivo do cabeçalho antes de salvar — sem ele a Meta recusa o template.';
  }
  if (anexo && url === '') delete config.headerMedia;
  return null;
};

const templateFormSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(200),
  description: z.string().max(1000).optional(),
  channelId: z.string().min(1, 'Canal é obrigatório'),
  providerConfig: z.any(),
  variables: z.array(z.any()).default([]),
});
type TemplateFormData = z.infer<typeof templateFormSchema>;

export const TemplateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [hasChanges, setHasChanges] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiError, setAiError] = useState('');

  const { register, handleSubmit, watch, setValue, formState: { errors, isDirty } } = useForm<TemplateFormData>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: { name: '', description: '', channelId: '', variables: [] },
  });

  const { data: template, isLoading: loadingTemplate } = useTemplate(id || '');
  const { mutate: createTemplate, isPending: creating } = useCreateTemplate();
  const { mutate: updateTemplate, isPending: updating } = useUpdateTemplate();
  const { data: channelsData, isLoading: loadingChannels } = useChannelsDropdown();
  const generateHtml = useGenerateHtml();

  useEffect(() => { setHasChanges(isDirty); }, [isDirty]);

  const selectedChannelId = watch('channelId');
  const selectedChannel = channelsData?.find((c) => c.id === selectedChannelId);
  const derivedProviderType = selectedChannel?.providerId || null;
  const isEmail = isEmailProvider(derivedProviderType);

  // AI HTML generation handler
  const handleAIGenerate = async (instructions: string) => {
    setAiError('');
    try {
      const currentHtml = watch('providerConfig.htmlBody') || '';
      const result = await generateHtml.mutateAsync({ instructions, currentHtml, templateId: id });
      setValue('providerConfig.htmlBody', result.html, { shouldDirty: true });
      setShowAIModal(false);
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Erro desconhecido');
    }
  };

  // Map providerId (hyphens) to providerType (underscores) for backend validation
  const providerIdToType: Record<string, string> = {
    'whatsapp-business': 'whatsapp_business',
    'gateway-whatsapp': 'whatsapp_gateway',
    'email-smtp': 'email_smtp',
    'email-sendgrid': 'email_sendgrid',
    'email-resend': 'email_resend',
    'email-ses': 'email_ses',
    'gmail-api': 'gmail_api',
    'instagram-messaging': 'instagram_direct',
    'facebook-messenger': 'facebook_messenger',
  };

  // Set providerType on providerConfig when channel changes (required by backend validation)
  useEffect(() => {
    if (derivedProviderType && !isEditMode) {
      const providerType = providerIdToType[derivedProviderType] || derivedProviderType;
      setValue('providerConfig.providerType', providerType);
    }
  }, [derivedProviderType, isEditMode, setValue]);

  useEffect(() => {
    if (template && isEditMode) {
      setValue('name', template.name);
      setValue('description', template.description || '');
      setValue('channelId', template.channelId);
      setValue('providerConfig', template.providerConfig);
      setValue('variables', template.variables);
    }
  }, [template, isEditMode, setValue]);

  const mapVariablesToBackend = (fv: VariableDefinition[]): TemplateVariable[] => {
    if (!fv || fv.length === 0) return [];
    return fv.map((v, i) => {
      if ((v as unknown as TemplateVariable).position && (v as unknown as TemplateVariable).label) {
        const tv = v as unknown as TemplateVariable;
        return { position: tv.position, label: tv.label, type: tv.type, required: tv.required ?? true, example: tv.example || `Exemplo ${tv.position}`, description: tv.description } as TemplateVariable;
      }
      const m = v.variable?.match(/\{\{(\d+)\}\}/);
      const pos = m ? parseInt(m[1]) : (i + 1);
      const tm: Record<string, 'string' | 'number' | 'date' | 'boolean'> = { text: 'string', number: 'number', date: 'date', email: 'string', phone: 'string', url: 'string', boolean: 'boolean' };
      return { position: pos, label: v.name || `Variável ${pos}`, type: tm[v.type] || 'string', required: v.required ?? true, example: v.example || `Exemplo ${pos}`, description: v.description } as TemplateVariable;
    });
  };

  const onSubmit = (data: TemplateFormData) => {
    if (data.providerConfig?.messageData && !data.providerConfig.messageData.type) data.providerConfig.messageData.type = 'text';

    // Ensure providerType is set if missing
    if (data.providerConfig && !data.providerConfig.providerType && derivedProviderType) {
      data.providerConfig.providerType = providerIdToType[derivedProviderType] || derivedProviderType;
    }

    // If providerConfig is incomplete (only providerType, no real content), let backend build default
    if (data.providerConfig && Object.keys(data.providerConfig).length <= 1) {
      data.providerConfig = undefined;
    }

    // Cabeçalho de mídia sem arquivo: barrar aqui, com o motivo em português.
    // O erro da Meta chega dias depois e não diz o que faltou.
    const erroDoAnexo = erroDoAnexoDoCabecalho(data.providerConfig as Record<string, unknown> | undefined);
    if (erroDoAnexo) {
      setActiveTab('provider');
      toast.error(erroDoAnexo);
      return;
    }
    if (isEditMode && id) {
      updateTemplate(
        { id, data: { name: data.name, description: data.description, providerConfig: data.providerConfig, variables: mapVariablesToBackend(data.variables as VariableDefinition[]) } },
        { onSuccess: () => navigate('/prospecting/templates'), onError: (e) => alert(`Erro: ${e.message}`) }
      );
    } else {
      createTemplate(
        { name: data.name, description: data.description, channelId: data.channelId, providerConfig: data.providerConfig, variables: mapVariablesToBackend(data.variables as VariableDefinition[]) },
        { onSuccess: () => navigate('/prospecting/templates'), onError: (e) => alert(`Erro: ${e.message}`) }
      );
    }
  };

  const tabs = useMemo<Array<{ id: TabType; name: string; icon: IconName }>>(() => {
    const t: Array<{ id: TabType; name: string; icon: IconName }> = [{ id: 'basic', name: 'Informações', icon: 'file-text' }];
    if (isEmail) {
      t.push({ id: 'email-content', name: 'Conteúdo do Email', icon: 'mail' });
    } else if (derivedProviderType) {
      t.push({ id: 'provider', name: 'Configuração', icon: 'settings' });
    }
    t.push({ id: 'variables', name: 'Variáveis', icon: 'hash' });
    return t;
  }, [isEmail, derivedProviderType]);

  const providerConfig = watch('providerConfig');
  const currentName = (watch('name') || '').trim() || (isEditMode ? 'Template' : 'Novo template');
  const watchedVariables = watch('variables');

  /**
   * Pré-visualização ao vivo — mesmo renderizador do chat, da campanha e dos
   * cards. Até aqui esta tela era a única sem preview nenhum: o usuário
   * montava o template às cegas.
   *
   * As variáveis do formulário existem em dois formatos (o do editor e o do
   * backend, quando o template é carregado para edição); `mapVariablesToBackend`
   * já normaliza os dois. O valor de exemplo de cada variável entra como valor
   * do preview, para o balão parecer com a mensagem real.
   */
  // `watch('providerConfig')` devolve a MESMA referência quando um campo
  // aninhado muda (`setValue('providerConfig.htmlBody', …)` muta o objeto no
  // lugar) — por isso a dependência é a foto serializada, senão o preview
  // ficava parado no conteúdo antigo (achado em 2026-09-09 com a aba HTML).
  const providerConfigSnapshot = JSON.stringify(providerConfig ?? null);
  const previewDoTemplate = useMemo(() => {
    const declaradas = mapVariablesToBackend((watchedVariables as VariableDefinition[]) || []);
    return renderTemplateSeguro(providerConfig, valoresDeExemplo(declaradas), declaradas);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mapVariablesToBackend é puro; providerConfig entra pela foto serializada
  }, [providerConfigSnapshot, watchedVariables]);

  if (isEditMode && loadingTemplate) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--theme-primary)' }} />
          <p style={{ color: 'var(--theme-muted-text)' }}>Carregando template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex overflow-hidden" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ══ LEFT: Tabs + Content ══════════════════════════════════ */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* ── Toolbar (DS 3 zonas: breadcrumb · ações) ─────────── */}
        <div
          className="flex-shrink-0 flex items-center gap-3 px-4"
          style={{ height: 52, backgroundColor: 'var(--theme-card-background)', borderBottom: '1px solid var(--theme-card-border)' }}
        >
          {/* Zona esquerda: breadcrumb (nunca seta-voltar) */}
          <nav className="flex items-center gap-1.5 min-w-0 flex-1" aria-label="breadcrumb">
            <button
              onClick={() => navigate('/prospecting/templates')}
              data-testid="template-form-breadcrumb-prospecting"
              className="text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
            >
              Prospecção
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <button
              onClick={() => navigate('/prospecting/templates')}
              data-testid="template-form-breadcrumb-templates"
              className="text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
            >
              Templates
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="text-base font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>{currentName}</span>
          </nav>

          {/* Zona direita: ações — Construir com IA (só na aba email-content) + Salvar/Criar */}
          {activeTab === 'email-content' && isEmail && (
            <button
              onClick={() => { setAiError(''); setShowAIModal(true); }}
              data-testid="template-form-improve-ai"
              className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-lg transition-colors flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-input-border)', color: 'var(--theme-card-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
            >
              <Icon name="zap" className="w-4 h-4" />
              <span className="hidden sm:inline">Construir com IA</span>
            </button>
          )}

          <button
            onClick={handleSubmit(onSubmit)}
            disabled={!hasChanges || creating || updating}
            data-testid="template-form-save"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 flex-shrink-0"
            style={{ backgroundColor: 'var(--theme-button-primary)', color: 'var(--theme-button-primary-text)' }}
            onMouseEnter={(e) => { if (hasChanges && !creating && !updating) e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)'; }}
          >
            <Icon name="save" className="w-4 h-4" />
            {isEditMode ? 'Salvar' : 'Criar'}
          </button>
        </div>

        {/* ── Tabs (shared, pageLevel — F55) + conteúdo por aba ── */}
        {(() => {
          const tabContent: Record<TabType, React.ReactNode> = {
            'email-content': (
              <EmailContentTab
                html={watch('providerConfig.htmlBody') || ''}
                previewHtml={previewDoTemplate?.corpoHtml ?? ''}
                onHtmlChange={(html) => setValue('providerConfig.htmlBody', html, { shouldDirty: true })}
                plainText={watch('providerConfig.plainTextBody') || ''}
                onPlainTextChange={(text) => setValue('providerConfig.plainTextBody', text, { shouldDirty: true })}
              />
            ),
            basic: (
              <div className="p-6">
                <div className="space-y-6 max-w-screen-2xl mx-auto">
              <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-card-text)' }}>Identificação</h3>
                <div className="p-4 rounded-lg space-y-4" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                  <div>
                    <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>Nome do Template *</label>
                    <Input
                      {...register('name')}
                      type="text"
                      placeholder="Ex: Boas-vindas WhatsApp"
                      error={errors.name?.message as string}
                      data-testid="template-form-name"
                    />
                    {errors.name && <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>{errors.name.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>Descrição</label>
                    <Textarea
                      {...register('description')}
                      rows={2}
                      placeholder="Descreva o propósito deste template..."
                      resize="none"
                      data-testid="template-form-description"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-card-text)' }}>Canal</h3>
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                  <Select
                    data-testid="template-form-channel"
                    label="Canal de envio"
                    required
                    description="Define o formato do template — só pode ser escolhido na criação"
                    placeholder={loadingChannels ? 'Carregando canais...' : 'Selecione um canal'}
                    options={channelsData?.map((c) => ({ value: c.id, label: c.name })) || []}
                    value={watch('channelId') || ''}
                    onChange={(val) => setValue('channelId', val, { shouldDirty: true })}
                    disabled={loadingChannels || isEditMode}
                    error={errors.channelId?.message as string}
                  />
                </div>
              </div>

              {/* Assunto do Email — só para providers de email */}
              {isEmail && (
                <div>
                  <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-card-text)' }}>Email</h3>
                  <div className="p-4 rounded-lg space-y-4" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                    <div>
                      <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>Assunto do Email *</label>
                      <Input
                        value={watch('providerConfig.subject') || ''}
                        onChange={(e) => setValue('providerConfig.subject', e.target.value, { shouldDirty: true })}
                        type="text"
                        placeholder="Ex: Bem-vindo à {{1}}! Confira suas vantagens"
                        data-testid="template-form-subject"
                      />
                    </div>
                  </div>
                </div>
              )}

                </div>
              </div>
            ),
            provider: (
              <div className="p-6">
                <div className="space-y-6 max-w-screen-2xl mx-auto">
              <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-card-text)' }}>Configuração do Provider</h3>
                <div className="p-4 rounded-lg space-y-4" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                  {derivedProviderType === 'whatsapp-business' && <WhatsAppBusinessForm register={register} watch={watch} setValue={setValue} errors={errors} />}
                  {derivedProviderType === 'gateway-whatsapp' && <GatewayForm register={register} watch={watch} setValue={setValue} errors={errors} />}
                  {(derivedProviderType === 'instagram-messaging' || derivedProviderType === 'facebook-messenger') && <SocialMediaForm register={register} watch={watch} setValue={setValue} errors={errors} />}
                  {!derivedProviderType && <div className="text-center py-8" style={{ color: 'var(--theme-muted-text)' }}>Selecione um canal na aba Informações</div>}
                </div>
              </div>
                </div>
              </div>
            ),
            variables: (
              <div className="p-6">
                <div className="space-y-6 max-w-screen-2xl mx-auto">
                  <div>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-card-text)' }}>Variáveis do Template</h3>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                      <VariableDefinitionEditor
                        value={(watch('variables') as VariableDefinition[]) || []}
                        onChange={(vars) => setValue('variables', vars as VariableDefinition[], { shouldDirty: true })}
                        maxVariables={10}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ),
          };

          const tabItems: TabItem[] = tabs.map((tab) => ({
            id: tab.id,
            label: tab.name,
            icon: tab.icon,
            content: tabContent[tab.id],
            'data-testid': `template-form-tab-${tab.id}`,
          }));

          return (
            <Tabs
              variant="underline"
              pageLevel
              fullBleed
              activeTab={activeTab}
              onChange={(t) => setActiveTab(t as TabType)}
              tabs={tabItems}
            />
          );
        })()}
      </div>

      {/* ══ RIGHT: pré-visualização ao vivo — não para e-mail, que já tem
          o preview inteiro na aba "Conteúdo do Email" (2026-09-09) ═══ */}
      {!isEmail && (
      <aside
        className="hidden lg:flex flex-col flex-shrink-0 w-[380px] overflow-y-auto"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderLeft: '1px solid var(--theme-card-border)',
        }}
        data-testid="template-form-preview"
      >
        <div
          className="flex-shrink-0 flex items-center px-4"
          style={{ height: 52, borderBottom: '1px solid var(--theme-card-border)' }}
        >
          <span className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
            Pré-visualização
          </span>
        </div>
        <div className="p-4">
          <TemplatePreview
            render={previewDoTemplate}
            providerType={providerConfig?.providerType as string | undefined}
            hideHeader
            fullWidthBubble
          />
        </div>
      </aside>
      )}

      {/* Modal Construir com IA */}
      <ImproveWithAIModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerate={handleAIGenerate}
        isLoading={generateHtml.isPending}
        error={aiError}
      />
    </div>
  );
};
